import { useEffect, useRef, useState } from 'react'
import { copy } from '@/copy'
import { useConsole } from '@/lib/store'
import { writeClipboard } from '@/lib/clipboard'

// A command in a terminal box with Copy: one line, never wrapping, scrolling
// inside its box and fading at the edge while there is more (the site's rule).
export function Terminal({ command }: { command: string }) {
  const box = useRef<HTMLDivElement>(null)
  const code = useRef<HTMLElement>(null)
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const announce = useConsole((s) => s.announce)

  useEffect(() => {
    const el = code.current
    const outer = box.current
    if (!el || !outer) return
    const mark = () => {
      const scrolls = el.scrollWidth > el.clientWidth + 1
      outer.toggleAttribute('data-scrolls', scrolls)
      outer.toggleAttribute('data-end', scrolls && el.scrollLeft + el.clientWidth >= el.scrollWidth - 1)
      if (scrolls) el.setAttribute('tabindex', '0')
      else el.removeAttribute('tabindex')
    }
    mark()
    el.addEventListener('scroll', mark, { passive: true })
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(mark)
    observer?.observe(el)
    return () => {
      el.removeEventListener('scroll', mark)
      observer?.disconnect()
    }
  }, [command])

  useEffect(() => {
    if (state === 'idle') return
    const t = setTimeout(() => setState('idle'), 2000)
    return () => clearTimeout(t)
  }, [state])

  const onCopy = async () => {
    const ok = await writeClipboard(command)
    setState(ok ? 'copied' : 'failed')
    announce(ok ? copy.shares.copied : copy.shares.copyFailed)
  }

  return (
    <div className="terminal" ref={box}>
      <code ref={code} aria-label={command}>
        <span className="prompt" aria-hidden="true">
          ${' '}
        </span>
        {command}
      </code>
      <button type="button" onClick={onCopy} aria-label={copy.copyCommandFor(command)}>
        {state === 'copied' ? copy.shares.copied : state === 'failed' ? copy.shares.copyFailed : copy.copyCommand}
      </button>
    </div>
  )
}
